/* Herlamora set page — item checkboxes + variant selects drive the
   live totals (full-set discount is display-only; the actual discount
   must exist as an automatic discount in the admin). The CTA adds all
   selected variants to the cart in one request. */

class HerlamoraSet extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-set-item]'));
    this.countLabel = this.querySelector('[data-set-count]');
    this.compareLabel = this.querySelector('[data-set-compare]');
    this.totalLabel = this.querySelector('[data-set-total]');
    this.savingsRow = this.querySelector('[data-set-savings]');
    this.savingsAmount = this.querySelector('[data-set-savings-amount]');
    this.ctaButton = this.querySelector('[data-set-add]');
    this.ctaLabel = this.querySelector('[data-set-cta]');
    this.inCartNote = this.querySelector('[data-set-incart]');
    this.message = this.querySelector('[data-set-message]');
    this.discountPercent = parseFloat(this.dataset.discountPercent || '0');
    this.buttonText = (this.ctaLabel?.textContent || '').split('—')[0].trim();

    this.items.forEach((item) => {
      item.querySelector('[data-item-toggle]')?.addEventListener('change', () => this.update());
      item.querySelector('[data-item-variant]')?.addEventListener('change', () => this.update());
    });
    this.ctaButton?.addEventListener('click', () => this.addToCart());

    this.update();
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return `€${amount}`;
  }

  selectedEntries() {
    return this.items
      .map((item) => {
        const toggle = item.querySelector('[data-item-toggle]');
        const select = item.querySelector('[data-item-variant]');
        if (!toggle || toggle.disabled || !toggle.checked || !select) return null;
        const option = select.selectedOptions[0];
        if (!option) return null;
        return { id: parseInt(option.value, 10), price: parseInt(option.dataset.price || '0', 10) };
      })
      .filter(Boolean);
  }

  selectableCount() {
    return this.items.filter((item) => {
      const toggle = item.querySelector('[data-item-toggle]');
      return toggle && !toggle.disabled;
    }).length;
  }

  update() {
    const entries = this.selectedEntries();
    const total = entries.reduce((sum, entry) => sum + entry.price, 0);
    // "Komplettset" = every item of the set, so a sold-out item also
    // disables the discount display (mirrors the server-side render)
    const allSelected = entries.length === this.items.length && entries.length > 0;
    const discounted = allSelected && this.discountPercent > 0;
    const finalTotal = discounted
      ? Math.round((total * (100 - this.discountPercent)) / 100)
      : total;
    const savings = total - finalTotal;

    // per-item price labels follow the selected variant
    this.items.forEach((item) => {
      const select = item.querySelector('[data-item-variant]');
      const label = item.querySelector('[data-item-price]');
      const option = select?.selectedOptions[0];
      if (label && option) label.textContent = this.formatMoney(parseInt(option.dataset.price || '0', 10));
      item.classList.toggle(
        'hst__item--off',
        !item.querySelector('[data-item-toggle]')?.checked
      );
    });

    if (this.countLabel) {
      this.countLabel.textContent = `${entries.length} von ${this.items.length} Teilen gewählt`;
    }
    if (this.compareLabel) {
      this.compareLabel.hidden = !discounted;
      this.compareLabel.textContent = this.formatMoney(total);
    }
    if (this.totalLabel) this.totalLabel.textContent = this.formatMoney(finalTotal);
    if (this.savingsRow) this.savingsRow.hidden = !discounted;
    if (this.savingsAmount) this.savingsAmount.textContent = this.formatMoney(savings);
    if (this.ctaLabel) {
      this.ctaLabel.textContent = entries.length > 0
        ? `${this.buttonText} — ${this.formatMoney(finalTotal)}`
        : this.buttonText;
    }
    if (this.ctaButton) this.ctaButton.disabled = entries.length === 0;
  }

  async addToCart() {
    const entries = this.selectedEntries();
    if (entries.length === 0) return;

    this.ctaButton.disabled = true;
    this.setMessage('Wird hinzugefügt ...', false);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: entries.map((entry) => ({ id: entry.id, quantity: 1 })),
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.description || 'Das Set konnte nicht hinzugefügt werden.');
      }

      // No redirect: Corner Cart intercepts the /cart/add.js call and
      // opens its sidebar itself. The event is a nudge for any other
      // cart UI (drawer, header count) listening for changes.
      this.ctaButton.disabled = false;
      this.setMessage('Das Set wurde zum Warenkorb hinzugefügt.', false);
      // The successful add response is all we need — no extra cart
      // request: a full-set add means the set is now completely in cart.
      if (this.inCartNote && entries.length === this.items.length) {
        this.inCartNote.hidden = false;
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (error) {
      this.ctaButton.disabled = false;
      this.setMessage(error.message || 'Das Set konnte nicht hinzugefügt werden.', true);
    }
  }

  setMessage(text, isError) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.hidden = !text;
    this.message.classList.toggle('hst__message--error', Boolean(isError));
  }
}

if (!customElements.get('herlamora-set')) {
  customElements.define('herlamora-set', HerlamoraSet);
}

/* Bild-Slider der Set-Seite: Scroll-Snap macht die Arbeit, die Pfeile
   schieben nur um eine Slide-Breite weiter und blenden sich an den Enden
   aus. Ohne JavaScript bleibt der Slider vollstaendig bedienbar -- dann
   fehlen lediglich die Pfeile. */
(() => {
  const init = (slider) => {
    const track = slider.querySelector('[data-set-slides]');
    const prev = slider.querySelector('[data-set-prev]');
    const next = slider.querySelector('[data-set-next]');
    if (!track || !prev || !next) return;

    const step = () => {
      const slide = track.querySelector('.hst__slide');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
    };

    const sync = () => {
      const max = track.scrollWidth - track.clientWidth - 1;
      prev.disabled = track.scrollLeft <= 0;
      next.disabled = track.scrollLeft >= max;
    };

    /* Nach dem Klick zusaetzlich nachziehen: scroll-Events koennen
       ausbleiben (z. B. in automatisierten Umgebungen), der Zustand der
       Pfeile soll aber trotzdem stimmen. */
    const move = (direction) => {
      track.scrollBy({ left: direction * step(), behavior: 'smooth' });
      setTimeout(sync, 400);
    };

    prev.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  };

  const boot = () => document.querySelectorAll('[data-set-slider]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
